import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Radio, ShoppingCart, Check, Zap, Wifi, Bluetooth, X, ChevronRight } from 'lucide-react';
import type { Accessory, MotorCatalogItem } from '../types';
import {
  getMotorHandbookCopy,
  getMotorHandbookMedia,
  resolveBackendMediaUrl,
  type AccessoryVitrineSection,
  type AccessoryVitrineTile,
  type MotorHandbookCopyMap,
  type MotorHandbookMediaMap,
} from '../api';
import { AccessoryPhotoOnWhite } from './AccessoryPhotoOnWhite';
import {
  MotorHandbookDetailSheet,
  type MotorHandbookDetailPayload,
} from './MotorHandbookDetailSheet';
import { resolveVitrineTileImage } from '../lib/vitrineTileImage';
import { accessoryColorSwatchClass } from '../lib/accessoryColorSwatch';
import { resolveMotorHandbookBody } from '../lib/motorHandbookCopy';
import {
  MOTOR_HANDBOOK_SECTIONS,
  motorHandbookModelKey,
} from '../lib/motorTechReference';

const springEase: [number, number, number, number] = [0.16, 1, 0.3, 1];

/** Fallback описаний для строк списка без description в БД. */
const ROW_DESC_FALLBACK: Record<string, string> = {
  'Датчик ветра': 'Автоматически убирает маркизы и ZIP-шторы при сильном ветре. Защита от повреждений.',
  'Белый пульт TUYA 1-канальный': 'Пульт ДУ для моторов TUYA. Работает через облако Tuya / Smart Life.',
  'Радиореле DC114': 'Радиочастотное реле для интеграции штор в системы умного дома через сухой контакт.',
  'Контроллер Умный дом BroadLink': 'Универсальный ИК/RF контроллер. Совместим с Яндекс Алисой, Google Home, Apple HomeKit.',
  'Черный ZigBee пульт управления': '15-канальный ZigBee пульт. Управление через хаб Tuya / Aqara.',
  'Хаб ZigBee Tuya с LAN портом': 'ZigBee шлюз с LAN-портом для стабильного локального подключения без облака.',
  'Хаб ZigBee Tuya': 'Компактный ZigBee шлюз. Подключение до 100 устройств, работа через приложение Tuya.',
  'Premium Черный Пульт управления Onviz (15 каналов)': 'Премиум 15-канальный пульт в матово-чёрном корпусе. Металлические кнопки, подсветка.',
};

// ─── Helpers ────────────────────────────────────────────────────────────────
function accIcon(name: string) {
  const n = name.toLowerCase();
  if (n.includes('wi-fi') || n.includes('tuya')) return <Wifi size={20} className="text-indigo-400" />;
  if (n.includes('zigbee') || n.includes('bluetooth')) return <Bluetooth size={20} className="text-purple-400" />;
  if (n.includes('пульт')) return <Radio size={20} className="text-blue-400" />;
  return <Zap size={20} className="text-amber-400" />;
}

// ─── AccessoriesTab component ────────────────────────────────────────────────
interface Props {
  accessories: Accessory[];
  vitrineSections?: AccessoryVitrineSection[];
  motors?: MotorCatalogItem[];
  onOpenMotor?: (groupId: string) => void;
  motorsOnly?: boolean;
  motorHandbookMedia?: MotorHandbookMediaMap;
  motorHandbookCopy?: MotorHandbookCopyMap;
  onMotorHandbookDataRefresh?: (media: MotorHandbookMediaMap, copy: MotorHandbookCopyMap) => void;
  extLoading: boolean;
  /** Ошибка загрузки списка аксессуаров с API (цены/корзина без данных). */
  accessoriesFetchError?: string | null;
  onRetryAccessoriesFetch?: () => void;
  addedIds: Set<number>;
  addingId: number | null;
  selectedGroup: string | null;
  setSelectedGroup: (g: string | null) => void;
  handleAddAccessory: (a: Accessory) => void;
}

export default function AccessoriesTab({
  motors = [],
  motorsOnly = false,
  motorHandbookMedia = {},
  motorHandbookCopy = {},
  onMotorHandbookDataRefresh,
  accessories,
  vitrineSections = [],
  extLoading,
  accessoriesFetchError = null,
  onRetryAccessoriesFetch,
  addedIds,
  addingId,
  selectedGroup,
  setSelectedGroup,
  handleAddAccessory,
}: Props) {
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [handbookDetail, setHandbookDetail] = useState<MotorHandbookDetailPayload | null>(null);

  const accById = useMemo(() => new Map(accessories.map((a) => [a.id, a])), [accessories]);
  const sections = vitrineSections ?? [];
  const allTiles = useMemo(() => sections.flatMap((s) => s.tiles), [sections]);
  const activeTile = allTiles.find((t) => t.slug === selectedGroup) ?? null;

  const openTile = (tile: AccessoryVitrineTile) => {
    setSelectedColor(null);
    setSelectedGroup(tile.slug);
  };
  const closeTile = () => setSelectedGroup(null);

  function rowDescription(name: string, description?: string | null): string | undefined {
    const d = (description || '').trim();
    if (d) return d;
    return ROW_DESC_FALLBACK[name];
  }

  function accessoryForVariantId(accessoryId: number): Accessory | null {
    return accById.get(accessoryId) ?? null;
  }

  function resolveActiveTile(tile: AccessoryVitrineTile): Accessory | null {
    if (!tile.variants.length) return null;
    const chosen =
      tile.variants.find((v) => v.label === selectedColor) ?? tile.variants[0];
    return accessoryForVariantId(chosen.accessory_id);
  }
  const motorsCount = motors.length;
  const handbookMediaCount = Object.keys(motorHandbookMedia || {}).length;
  const openMotorHandbookCard = (
    sectionTitle: string,
    model: (typeof MOTOR_HANDBOOK_SECTIONS)[number]['models'][number],
    imageKey: string,
  ) => {
    void (async () => {
      let media = motorHandbookMedia;
      let copy = motorHandbookCopy;
      try {
        const [freshMedia, freshCopy] = await Promise.all([
          getMotorHandbookMedia(),
          getMotorHandbookCopy(),
        ]);
        media = freshMedia || {};
        copy = freshCopy || {};
        onMotorHandbookDataRefresh?.(media, copy);
      } catch {
        /* оставляем кэш с экрана */
      }
      const rawUrl = String(media[imageKey] || '').trim();
      const resolved = rawUrl ? resolveBackendMediaUrl(rawUrl) || rawUrl : '';
      setHandbookDetail({
        modelName: model.name,
        sectionTitle,
        imageUrl: resolved || undefined,
        body: resolveMotorHandbookBody(copy[imageKey], model),
      });
    })();
  };

  const handbookSheet = handbookDetail ? (
    <MotorHandbookDetailSheet detail={handbookDetail} onClose={() => setHandbookDetail(null)} />
  ) : null;

  if (motorsOnly) {
    return (
      <div className="p-4">
        {extLoading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--cyan)', borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <>
            <div className="glass-accent relative rounded-2xl overflow-hidden mb-5 p-5">
              <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                <Zap size={128} className="text-white" />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--text-2)' }}>Каталог моторов</p>
              <p className="font-extrabold text-xl leading-tight" style={{ color: 'var(--text-1)' }}>Моторы Onviz</p>
              <p className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
                Технический справочник: 4 типа моторов, подтипы и ключевые характеристики для ознакомления.
              </p>
              <p className="text-[10px] mt-1" style={{ color: 'var(--text-3)' }}>
                Витринных моделей в API: {motorsCount}
              </p>
            </div>
            <div className="space-y-3 mb-4">
              {handbookMediaCount === 0 ? (
                <div
                  className="rounded-xl p-3 text-[11px] leading-relaxed"
                  style={{ background: 'rgba(252,129,74,0.12)', border: '1px solid rgba(252,129,74,0.35)', color: '#F6AD55' }}
                >
                  Технички пока не загружены. Настройка: <strong>Админка → Каталог → Пульты → Технички моторов</strong>.
                </div>
              ) : null}
              {MOTOR_HANDBOOK_SECTIONS.map((section) => {
                return (
                  <div
                    key={section.id}
                    className="rounded-2xl p-3.5"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}
                  >
                    <p className="text-sm font-extrabold" style={{ color: 'var(--text-1)' }}>
                      {section.title}
                    </p>
                    <p className="text-[10px] mt-1 leading-relaxed" style={{ color: 'var(--text-3)' }}>
                      Источник: {section.sourceUrl}
                    </p>
                    <p className="text-[11px] mt-2 font-semibold" style={{ color: 'var(--text-2)' }}>
                      Подтипов: {section.models.length}
                    </p>
                    <div className="mt-3 space-y-2">
                      {section.models.map((model) => {
                        const imageKey = motorHandbookModelKey(section.id, model.name);
                        const imageUrl = motorHandbookMedia[imageKey];
                        const resolvedHandbookImage = imageUrl ? (resolveBackendMediaUrl(imageUrl) || imageUrl) : '';
                        return (
                        <div
                          key={`${section.id}-${model.name}`}
                          className="rounded-xl p-2.5"
                          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}
                        >
                          <p className="text-xs font-bold" style={{ color: 'var(--text-1)' }}>
                            {model.name}
                          </p>
                          {model.summary ? (
                            <p className="text-[10px] mt-1 leading-snug" style={{ color: 'var(--text-3)' }}>
                              {model.summary}
                            </p>
                          ) : null}
                          <div className="mt-1.5 space-y-1">
                            {model.specs.map((spec) => (
                              <p key={`${section.id}-${model.name}-${spec}`} className="text-[10px] leading-snug" style={{ color: 'var(--text-2)' }}>
                                • {spec}
                              </p>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="press mt-2.5 w-full py-2 rounded-lg text-[10px] font-semibold"
                            style={{
                              color: 'var(--cyan-bright)',
                              background: 'rgba(99,179,237,0.08)',
                              border: '1px solid var(--cyan-border)',
                            }}
                            onClick={() => openMotorHandbookCard(section.title, model, imageKey)}
                          >
                            {resolvedHandbookImage ? 'Открыть карточку' : 'Характеристики'}
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {handbookSheet}
      </div>
    );
  }

  return (
    <div className="p-4">
      {accessoriesFetchError && (
        <div
          className="rounded-xl px-3 py-2.5 mb-4 space-y-2"
          style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}
          role="alert"
        >
          <p className="text-[11px] leading-snug">{accessoriesFetchError}</p>
          {typeof onRetryAccessoriesFetch === 'function' && (
            <button
              type="button"
              className="press w-full py-2 rounded-lg text-[11px] font-bold"
              style={{ background: 'rgba(252,129,74,0.2)', border: '1px solid rgba(252,129,74,0.45)' }}
              onClick={() => onRetryAccessoriesFetch()}
            >
              Повторить загрузку
            </button>
          )}
        </div>
      )}
      {extLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--cyan)', borderTopColor: 'transparent' }} />
        </div>
      ) : (
        <>
          {/* ── Hero banner ── */}
          <div className="glass-accent relative rounded-2xl overflow-hidden mb-5 p-5">
            <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
              <Radio size={128} className="text-white" />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--text-2)' }}>Пульты и аксессуары</p>
            <p className="font-extrabold text-xl leading-tight" style={{ color: 'var(--text-1)' }}>Управление шторами</p>
            <p className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>Пульты ДУ, Wi-Fi и ZigBee контроллеры, хабы</p>
          </div>

          {sections.map((section) => (
            <section key={section.label} className="mb-6">
              <p className="text-xs font-bold uppercase tracking-wider mb-3 px-1" style={{ color: 'var(--text-3)' }}>
                {section.label}
              </p>

              {section.tiles.length > 0 && (
                <motion.div
                  className="grid grid-cols-2 gap-3 mb-4"
                  initial="hidden"
                  animate="show"
                  variants={{
                    hidden: {},
                    show: { transition: { staggerChildren: 0.05, delayChildren: 0.04 } },
                  }}
                >
                  {section.tiles.map((tile) => {
                    const tileThumb = resolveVitrineTileImage(tile);
                    const tileThumbResolved = tileThumb
                      ? resolveBackendMediaUrl(tileThumb) || tileThumb
                      : '';
                    return (
                    <motion.button
                      key={tile.slug}
                      type="button"
                      onClick={() => openTile(tile)}
                      className="rounded-2xl overflow-hidden flex flex-col text-left active:scale-[0.97] transition-transform"
                      style={{
                        border: '1px solid var(--cyan-border)',
                        boxShadow: '0 0 0 1px rgba(99,179,237,0.08)',
                        background: 'rgba(255,255,255,0.05)',
                      }}
                      variants={{
                        hidden: { opacity: 0, y: 18, scale: 0.96 },
                        show: {
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          transition: { duration: 0.42, ease: springEase },
                        },
                      }}
                    >
                      <div
                        className="h-[7.5rem] flex items-center justify-center px-2 py-2 relative w-full"
                        style={{ background: '#ffffff' }}
                      >
                        <AccessoryPhotoOnWhite
                          src={tileThumbResolved}
                          alt={tile.title}
                          className="max-h-full max-w-full object-contain"
                        />
                        {tile.variants.length > 1 && (
                          <div className="absolute bottom-2 right-2 flex gap-1">
                            {tile.variants.map((v) => (
                              <span
                                key={v.label}
                                className={`w-3 h-3 rounded-full border ${accessoryColorSwatchClass(v.label)}`}
                                style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <div
                        className="p-3 flex flex-col flex-1 glass-light"
                        style={{ borderTop: '1px solid var(--border-subtle)' }}
                      >
                        <p className="text-[11px] font-bold leading-tight" style={{ color: 'var(--text-1)' }}>{tile.title}</p>
                        {tile.subtitle ? (
                          <p className="text-[10px] mt-0.5 leading-tight line-clamp-1" style={{ color: 'var(--text-3)' }}>{tile.subtitle}</p>
                        ) : null}
                        <div className="flex items-center justify-between mt-2">
                          {typeof tile.min_price === 'number' ? (
                            <p className="text-xs font-extrabold" style={{ color: 'var(--cyan-bright)' }}>
                              от {tile.min_price.toLocaleString('ru-RU')} ₽
                            </p>
                          ) : (
                            <p className="text-[10px] font-semibold" style={{ color: 'var(--text-3)' }}>Уточняйте у менеджера</p>
                          )}
                          <ChevronRight size={14} style={{ color: 'var(--text-3)' }} />
                        </div>
                      </div>
                    </motion.button>
                    );
                  })}
                </motion.div>
              )}

              {section.rows.length > 0 && (
                <motion.div
                  className="space-y-2"
                  initial="hidden"
                  animate="show"
                  variants={{
                    hidden: {},
                    show: { transition: { staggerChildren: 0.04, delayChildren: 0.05 } },
                  }}
                >
                  {section.rows.map((row) => {
                    const acc = accessoryForVariantId(row.id);
                    const desc = rowDescription(row.name, row.description);
                    return (
                      <motion.div
                        key={row.id}
                        className="glass-light rounded-2xl flex items-center gap-3 p-3"
                        variants={{
                          hidden: { opacity: 0, y: 14, scale: 0.98 },
                          show: {
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            transition: { duration: 0.38, ease: springEase },
                          },
                        }}
                      >
                        <div className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0 overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          {row.image_url ? (
                            <img
                              src={resolveBackendMediaUrl(row.image_url) || ''}
                              alt={row.name}
                              className="w-full h-full object-contain p-1"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="opacity-50">{accIcon(row.name)}</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold leading-snug" style={{ color: 'var(--text-1)' }}>{row.name}</p>
                          {desc ? (
                            <p className="text-[10px] mt-0.5 leading-tight line-clamp-2" style={{ color: 'var(--text-3)' }}>{desc}</p>
                          ) : null}
                          <p className="text-sm font-extrabold mt-1" style={{ color: 'var(--cyan-bright)' }}>
                            {typeof row.price === 'number' ? `${row.price.toLocaleString('ru-RU')} ₽` : '—'}
                          </p>
                        </div>
                        {acc ? (
                          <button
                            type="button"
                            onClick={() => handleAddAccessory(acc)}
                            disabled={addingId === acc.id}
                            className={`shrink-0 h-9 px-3 rounded-xl text-xs font-bold flex items-center gap-1.5 text-white ${
                              addedIds.has(acc.id) ? 'bg-green-500' : ''
                            }`}
                            style={
                              addedIds.has(acc.id)
                                ? undefined
                                : { background: 'linear-gradient(135deg,#1a6eb5,#0e4a8c)', border: '1px solid var(--cyan-border)' }
                            }
                          >
                            {addedIds.has(acc.id) ? (
                              <Check size={13} />
                            ) : addingId === acc.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <ShoppingCart size={13} />
                            )}
                          </button>
                        ) : null}
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </section>
          ))}

        </>
      )}

      {/* ── Group detail modal ── */}
      {activeTile && (() => {
        const activeColor = selectedColor ?? activeTile.variants[0]?.label ?? null;
        const activeVariant =
          activeTile.variants.find((v) => v.label === activeColor) ?? activeTile.variants[0];
        const defaultColor = activeTile.variants[0]?.label ?? null;
        const colorSelected = selectedColor != null && selectedColor !== defaultColor;
        const activeImgUrl = resolveVitrineTileImage(activeTile, {
          variant: activeVariant,
          colorSelected,
        });
        const activeImgResolved = activeImgUrl ? (resolveBackendMediaUrl(activeImgUrl) || activeImgUrl) : '';
        return (
        <div className="fixed inset-0 z-50 bg-black/60 flex flex-col justify-end" onClick={closeTile}>
          <div
            className="rounded-t-3xl flex flex-col overflow-hidden"
            style={{
              maxHeight: '92dvh',
              height: '92dvh',
              background: 'rgba(10,25,47,0.96)',
              borderTop: '1px solid var(--cyan-border)',
              boxShadow: '0 0 0 1px rgba(99,179,237,0.12)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-end px-4 pt-3 pb-1 shrink-0">
              <button
                type="button"
                onClick={closeTile}
                className="w-8 h-8 rounded-full flex items-center justify-center press"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-subtle)' }}
              >
                <X size={16} style={{ color: 'var(--text-2)' }} />
              </button>
            </div>

            {/* Белая зона — как на плитке в сетке */}
            <div
              className="w-full shrink-0 overflow-hidden"
              style={{ background: '#ffffff', minHeight: '14rem' }}
            >
              <AccessoryPhotoOnWhite
                src={activeImgResolved}
                alt={activeTile.title}
                className="max-h-[14rem] w-full object-contain px-3 py-4"
              />
            </div>

            <div className="flex-1 overflow-y-auto glass-light" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <div className="px-5 pt-4 pb-2">
              <h2 className="text-lg font-extrabold leading-tight" style={{ color: 'var(--text-1)' }}>{activeTile.title}</h2>
              {activeTile.subtitle ? (
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>{activeTile.subtitle}</p>
              ) : null}
            </div>

            {(() => {
              const variantDesc = (activeVariant?.description || '').trim();
              const text = variantDesc || (activeTile.description || '').trim();
              return text ? (
                <p className="px-5 text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{text}</p>
              ) : null;
            })()}

            {activeTile.variants.length > 1 && (
              <div className="px-5 mt-4">
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-3)' }}>Цвет</p>
                <div className="flex gap-2">
                  {activeTile.variants.map(v => {
                    const isActive = (selectedColor ?? activeTile.variants[0].label) === v.label;
                    return (
                      <button
                        key={v.label}
                        onClick={() => setSelectedColor(v.label)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 text-xs font-semibold transition-all ${
                          isActive ? '' : ''
                        }`}
                        style={isActive
                          ? { borderColor: 'var(--cyan)', background: 'rgba(99,179,237,0.18)', color: 'var(--cyan-bright)' }
                          : { borderColor: 'var(--border-subtle)', color: 'var(--text-2)' }}
                      >
                        <span className={`w-3.5 h-3.5 rounded-full border ${accessoryColorSwatchClass(v.label)}`} />
                        {v.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Price + Add to cart */}
            {(() => {
              const activeAcc = resolveActiveTile(activeTile);
              return activeAcc ? (
                <div className="px-5 mt-5 pb-6 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>Цена</p>
                    <p className="text-2xl font-extrabold" style={{ color: 'var(--cyan-bright)' }}>{activeAcc.price?.toLocaleString('ru-RU')} ₽</p>
                  </div>
                  <button
                    onClick={() => handleAddAccessory(activeAcc)}
                    disabled={addingId === activeAcc.id}
                    className={`flex-1 h-12 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-colors text-white ${
                      addedIds.has(activeAcc.id) ? 'bg-green-500' : ''
                    }`}
                    style={addedIds.has(activeAcc.id) ? undefined : { background: 'linear-gradient(135deg,#1a6eb5,#0e4a8c)', border: '1px solid var(--cyan-border)' }}
                  >
                    {addedIds.has(activeAcc.id)
                      ? <><Check size={16} /> Добавлено в корзину</>
                      : addingId === activeAcc.id
                      ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <><ShoppingCart size={16} /> Добавить в корзину</>
                    }
                  </button>
                </div>
              ) : (
                <div className="px-5 mt-4 pb-6">
                  <p className="text-xs" style={{ color: 'var(--text-3)' }}>Уточните наличие у менеджера</p>
                </div>
              );
            })()}
            </div>
          </div>
        </div>
        );
      })()}

      {handbookSheet}
    </div>
  );
}
